import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  useMemo,
  useEffect,
} from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useYupValidation, { useHandleValidate, useYupSchemas } from "../../hooks/useYupValidation";
import { Cronjob } from "../../models/cronjob";
import { capitalizeFirstLetter } from "../../util/capitalizeFirstLetter";
import { CronjobFrequency, OptionsSwitch, Status, WeekDays } from "../../util/enums";
import { Input, Select } from "../Form";
import AutoSuggest from "../Form/AutoSuggest";
import SkeletonComponent from "../Generic/Skeleton";
import { FormLabel, Modal, RadioButton, RadioLabel, StyledRadioGroup } from "./styles";

export interface SendEmailHandles {
  open: (cronjob?: Cronjob) => void;
  close: () => void;
}

interface Props {
  onSubmit: (cronjob: Cronjob, isCreateSendRecurrence: boolean) => void;
  isLoading?: boolean;
  title?: string;
}

const SendEmailModal: ForwardRefRenderFunction<SendEmailHandles, Props> = (props, ref) => {
  const { onSubmit, isLoading, title } = props;
  const { t: translate } = useTranslation();

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [cronjob, setCronjob] = useState<Cronjob>();
  const [createSendRecurrence, setCreateSendRecurrence] = useState<string>(OptionsSwitch.NO);
  const isUpdate = useMemo(() => !!cronjob?.id, [cronjob?.id]);
  const isCreateSendRecurrence = createSendRecurrence === OptionsSwitch.YES || isUpdate;

  const monthDaysList = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), []);
  const data = useMemo(() => {
    return {
      ...cronjob,
      contactEmails: cronjob?.contactEmails ?? [],
      isUpdate,
      dayRequired: cronjob?.frequency !== CronjobFrequency.DAILY && isCreateSendRecurrence,
      isCreateSendRecurrence,
    };
  }, [cronjob, isCreateSendRecurrence]);

  const clearFields = () => {
    setCronjob(undefined);
    setCreateSendRecurrence(OptionsSwitch.NO);
  };

  const clearErrors = () => {
    setErrors([]);
    setHasFormBeenSubmitted(false);
  };

  const onClearRecurrence = () => {
    setCronjob(
      (prev) => ({ ...prev, frequency: undefined, time: undefined, day: undefined } as Cronjob)
    );
  };

  const openModal = useCallback((cronjobTopdate?: Cronjob) => {
    setModalOpened(true);
    if (cronjobTopdate) setCronjob(cronjobTopdate);
  }, []);
  const closeModal = useCallback(() => {
    clearFields();
    clearErrors();
    setCronjob(undefined);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    open: openModal,
    close: closeModal,
  }));

  const handleChangeRecurrence = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value !== OptionsSwitch.YES) onClearRecurrence();
    setCreateSendRecurrence((event.target as HTMLInputElement).value);
  };
  const getFirstErrorFromArray = (key: string) => {
    const currentError = errors.find((err) => err.path?.includes(key));

    return currentError?.message;
  };

  const { stringRequired, numberNullable, numberRequired } = useYupSchemas(translate);
  const emailRequiredMessage = `${translate("thisFieldIsRequired")} (${translate(
    "typeTheEmailAddressEnter"
  )})`;
  const schema = yup.object().shape({
    name: yup.string().nullable().when("isCreateSendRecurrence", {
      is: true,
      then: stringRequired,
    }),
    contactEmails: yup
      .array()
      .min(1, emailRequiredMessage)
      .transform((value, originalValue) => {
        if (value && value !== null) {
          return value;
        }

        return originalValue ? originalValue.split(/[\s,]+/) : [];
      })
      .of(
        yup
          .string()
          .min(5, "longer")
          .email(({ value }) => `${value} ${translate("isNotAValidEmail")}`)
      ),
    frequency: yup.string().nullable().when("isCreateSendRecurrence", {
      is: true,
      then: stringRequired,
    }),
    time: yup.string().nullable().when("isCreateSendRecurrence", {
      is: true,
      then: stringRequired,
    }),
    day: numberNullable.when("dayRequired", {
      is: true,
      then: numberRequired,
    }),
  });

  const isValid = () => {
    const validated = useHandleValidate(data, schema, setErrors);

    return validated;
  };

  const frequencyOptions = [
    {
      label: translate(CronjobFrequency.DAILY),
      value: CronjobFrequency.DAILY,
    },
    {
      label: translate(CronjobFrequency.WEEKLY),
      value: CronjobFrequency.WEEKLY,
    },
    {
      label: translate(CronjobFrequency.MONTHLY),
      value: CronjobFrequency.MONTHLY,
    },
  ];

  const weekDaysOptions = [
    {
      label: translate(WeekDays.SUNDAY),
      value: 0,
    },
    {
      label: translate(WeekDays.MONDAY),
      value: 1,
    },
    {
      label: translate(WeekDays.TUESDAY),
      value: 2,
    },
    {
      label: translate(WeekDays.WEDNESDAY),
      value: 3,
    },
    {
      label: translate(WeekDays.THURSDAY),
      value: 4,
    },
    {
      label: translate(WeekDays.FRIDAY),
      value: 5,
    },
    {
      label: translate(WeekDays.SATURDAY),
      value: 6,
    },
  ];

  const handleSubmit = () => {
    setHasFormBeenSubmitted(true);

    if (isValid()) {
      if (cronjob) {
        onSubmit(cronjob, isCreateSendRecurrence);
      }
    }
  };

  const baseFieldProps = (key: keyof Cronjob) => {
    return {
      name: key,
      disabled: isLoading,
      label: translate(key),
      value: cronjob?.[key],
      dataCy: key,
      placeholder: translate(key),
      error: fieldHasError(key),
      errorMessage: getErrorByField(key),
      required: true,
    };
  };

  useEffect(() => {
    if (hasFormBeenSubmitted) isValid();
  }, [hasFormBeenSubmitted, ...Object.keys(data).map((key) => data[key as keyof typeof data])]);

  return (
    <Modal
      title={title ?? translate("sendReportByEmail")}
      open={modalOpened}
      handleClose={closeModal}
      showModalFooter
      showActionButtons
      actionButtonProps={{
        loading: isLoading,
        onCancel: closeModal,
        onSave: handleSubmit,
        saveDisabled: isLoading,
        showSaveButton: true,
        showCancelButton: true,
        textSave: isUpdate ? translate("save") : translate("send"),
      }}
      labelStatus={translate("status")}
      showStatusField={isUpdate}
      statusFieldProps={{
        ...baseFieldProps("isRunning"),
        label: translate("status"),
        value: cronjob?.isRunning ? Status.ACTIVE : Status.INACTIVE,
        placeholder: translate("selectAnOption"),
        handleChange: (value) =>
          setCronjob((prev) => ({ ...prev, isRunning: value === Status.ACTIVE } as Cronjob)),
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={2}
        data-cy="send-shipment-email-modal"
      >
        {isCreateSendRecurrence && (
          <Grid item sm={9}>
            <Input
              {...baseFieldProps("name")}
              type="text"
              value={cronjob?.name}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCronjob((prev) => ({ ...prev, name: event.target.value } as Cronjob));
              }}
            />
          </Grid>
        )}
        <Grid item sm={9} data-cy="send-shipment-email-modal-input">
          <SkeletonComponent isLoading={false}>
            <AutoSuggest
              {...baseFieldProps("contactEmails")}
              placeholder={translate("typeTheEmailAddressEnter")}
              value={cronjob?.contactEmails ?? []}
              label={capitalizeFirstLetter(translate("emails"))}
              multiple
              freeSolo
              removeShadowBox={false}
              getOptionLabel={(option: string) => option}
              onChange={(emails: string[]) => {
                setCronjob((prev) => ({ ...prev, contactEmails: emails } as Cronjob));
              }}
              error={!!getFirstErrorFromArray("contactEmails")}
              errorMessage={getFirstErrorFromArray("contactEmails")}
            />
          </SkeletonComponent>
        </Grid>
        {!isUpdate ? (
          <Grid container item sm={12} data-cy="send-shipment-email-modal-input">
            <Grid item sm={12}>
              <FormLabel>{translate("repeat")}</FormLabel>
            </Grid>
            <Grid container item sm={12} data-cy="createSendRecurrenceContainner">
              <StyledRadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={createSendRecurrence || ""}
                onChange={handleChangeRecurrence}
              >
                <RadioLabel
                  value={OptionsSwitch.YES}
                  control={<RadioButton disabled={isLoading} />}
                  label={translate(OptionsSwitch.YES)}
                />
                <RadioLabel
                  value={OptionsSwitch.NO}
                  control={<RadioButton disabled={isLoading} />}
                  label={translate(OptionsSwitch.NO)}
                />
              </StyledRadioGroup>
            </Grid>
          </Grid>
        ) : null}
        {isCreateSendRecurrence && (
          <Grid item sm={5} md={5}>
            <Select
              {...baseFieldProps("frequency")}
              data-cy="frequency"
              name="frequency"
              label={translate("frequency")}
              error={fieldHasError("frequency")}
              errorMessage={getErrorByField("frequency")}
              placeholder={`${translate("select")}...`}
              value={cronjob?.frequency}
              onChange={(e: SelectChangeEvent<any>) => {
                setCronjob((prev) => ({ ...prev, frequency: e.target.value } as Cronjob));
                setCronjob((prev) => ({ ...prev, day: undefined } as Cronjob));
              }}
            >
              {frequencyOptions.map((option) => (
                <MenuItem data-cy={option.value} value={option.value} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {isCreateSendRecurrence && cronjob?.frequency === CronjobFrequency.WEEKLY && (
          <Grid item sm={4} md={4}>
            <Select
              {...baseFieldProps("day")}
              data-cy="weekDay"
              name="weekDay"
              label={translate("sendEvery")}
              placeholder={`${translate("select")}...`}
              value={String(cronjob.day)}
              onChange={(event: SelectChangeEvent<any>) => {
                setCronjob((prev) => ({ ...prev, day: Number(event.target.value) } as Cronjob));
              }}
            >
              {weekDaysOptions.map((option) => (
                <MenuItem data-cy={option.label} value={String(option.value)} key={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {isCreateSendRecurrence && cronjob?.frequency === CronjobFrequency.MONTHLY && (
          <Grid item sm={4} md={4}>
            <Select
              {...baseFieldProps("day")}
              data-cy="monthDay"
              name="monthDay"
              label={translate("date")}
              placeholder={`${translate("select")}...`}
              value={cronjob.day}
              onChange={(event: SelectChangeEvent<any>) =>
                setCronjob((prev) => ({ ...prev, day: Number(event.target.value) } as Cronjob))
              }
            >
              {monthDaysList.map((option) => (
                <MenuItem data-cy={option} value={option} key={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}
        {isCreateSendRecurrence && (
          <Grid item sm={3} md={3}>
            <Input
              {...baseFieldProps("time")}
              type="time"
              placeholder={`${translate("select")}...`}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setCronjob((prev) => ({ ...prev, time: event.target.value } as Cronjob));
              }}
            />
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};

export default forwardRef(SendEmailModal);
